import { Document, Packer, Paragraph, AlignmentType, HeadingLevel, ImageRun, TextRun, Table, TableRow, TableCell, WidthType, BorderStyle, TableLayoutType, PositionalTab, PositionalTabAlignment, PositionalTabRelativeTo, PositionalTabLeader } from 'docx';
import { saveAs } from 'file-saver';
import moment from 'moment';
import Logo from "../../../../styles/img/CompliNYC-Logo.png";

// Helper function to create proof of mailing table
const createProofOfMailingTable = (unitsData, buildingData,clientData) => {
  const tableRows = [];
  
  // Table header
  tableRows.push(
    new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({ text: "Addressee", bold: true, underline: {} }),
              ],
            }),
          ],
          width: { size: 15, type: WidthType.PERCENTAGE },
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({ text: "Apt#", bold: true, underline: {} }),
              ],
            }),
          ],
          width: { size: 15, type: WidthType.PERCENTAGE },
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({ text: "Address", bold: true, underline: {} }),
              ],
            }),
          ],
          width: { size: 50, type: WidthType.PERCENTAGE },
        }),
      ],
    })
  );

  // Add unit rows
  unitsData.forEach((unit) => {
    tableRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({ text: "Resident of "}),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({ text: unit.unit_number || "" }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: `${buildingData.address}, ` }),
                  new TextRun({
                    text: `${buildingData.city}, ${buildingData.state} ${buildingData.zip_code}`,
                  }),
                ],
              }),
            ],
          }),
        ],
      })
    );
  });

  return new Table({
    rows: tableRows,
    width: { size: 100, type: WidthType.PERCENTAGE },
    layout: TableLayoutType.FIXED,
    columnWidths: [3000, 3000, 5000],
    borders: {
      top: { style: BorderStyle.SINGLE },
      bottom: { style: BorderStyle.SINGLE },
      left: { style: BorderStyle.SINGLE },
      right: { style: BorderStyle.SINGLE },
      insideHorizontal: { style: BorderStyle.SINGLE },
      insideVertical: { style: BorderStyle.SINGLE },
    },
  });
};

export const generateAffidavitDocument = async (clientData, buildingData, unitsData = []) => {
  if (!clientData || !buildingData) {
    throw new Error("Client and building data are required");
  }

  // Create document with two sections
  const doc = new Document({
    sections: [
      // First section - Affidavit page
      {
        properties: {},
        children: [
          // Logo and company info header
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new ImageRun({
                data: await fetch(Logo).then((res) => res.arrayBuffer()),
                transformation: { width: 340, height: 80 },
              }),
            ],
          }),

          // Company address
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: "35 E. Grassy Sprain Road, Suite 302, Yonkers, New York 10710, Phone: 845-366-3431",
                size: 18, // 9pt
              }),
            ],
            spacing: { after: 240 },
          }),

          // Document title
          new Paragraph({
            text: "AFFIDAVIT OF MAILING",
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            bold: true,
            spacing: { after: 240 },
          }),

          // Property address
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: "Property Address: ",
                bold: true,
              }),
              new TextRun({
                text:
                  buildingData.address +
                  ", " +
                  buildingData.city +
                  ", " +
                  buildingData.state +
                  " " +
                  buildingData.zip_code,
              }),
            ],
            spacing: { after: 120 },
          }),

          // Agent/Owner info
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: "Agent/Owner - ",
                bold: true,
              }),
              new TextRun({
                text: `${clientData.user.name}, ${
                  clientData.address + ", " + clientData?.city ||
                  "" + ", " + clientData?.state ||
                  "" + " " + clientData?.zip_code ||
                  ""
                }`,
              }),
            ],
            spacing: { after: 240 },
          }),

          // Affidavit body - first paragraph
          new Paragraph({
            children: [
              new TextRun({
                text: "I, ________________________being duly sworn deposes and says under penalties",
              }),
            ],
            spacing: { after: 120 },
          }),

          // Of perjury line
          new Paragraph({
            text: "Of perjury:",
            spacing: { after: 240 },
          }),

          // Employment paragraph
          new Paragraph({
            children: [
              new TextRun({
                text: "I am currently employed by ",
              }),
              new TextRun({
                text: "CompliNYC",
                bold: true,
              }),
              new TextRun({
                text: " which has been retained by ",
              }),
              new TextRun({
                text: buildingData.building_corporation,
                bold: true,
              }),
              new TextRun({
                text: " to file certain annual notices on behalf of the above referenced property agent.",
              }),
            ],
            spacing: { after: 240 },
          }),

          // Mailing paragraph
          new Paragraph({
            children: [
              new TextRun({
                text: "On, __________________ I deposited in a post office box located in New York City, a properly stamped and sealed envelope in an official depository under the exclusive care and custody of the U.S. Postal Service within New York State addressed to the property referenced above to be delivered to the occupant of each of the apartments at the addresses listed in the attached.",
              }),
            ],
            spacing: { after: 240 },
          }),

          // Signature line
          new Paragraph({
            children: [
              new TextRun({
                text: "Signed: _________________________",
              }),
            ],
            spacing: { after: 240 },
          }),

          // Sworn statement
          new Paragraph({
            children: [
              new TextRun({
                text: "Sworn to before me this _______day",
              }),
            ],
            spacing: { after: 120 },
          }),

          // Date line
          new Paragraph({
            children: [
              new TextRun({
                text: "Of _______________________, 202___",
              }),
            ],
            spacing: { after: 240 },
          }),

          // Notary line
          new Paragraph({
            children: [
              new TextRun({
                text: "Notary Public",
              }),
            ],
          }),
        ],
      },
      // Second section - Proof of Mailing page
      {
        properties: {},
        children: [
          // Page break
          new Paragraph({
            children: [new TextRun({ text: "", break: 1 })],
          }),

          // Logo and company info header (second page)
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new ImageRun({
                data: await fetch(Logo).then((res) => res.arrayBuffer()),
                transformation: { width: 340, height: 80 },
              }),
            ],
          }),

          // Document title for second page
          new Paragraph({
            text: `${new Date().getFullYear()} ANNUAL SAFETY NOTICE PROOF OF MAILING`,
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            bold: true,
            spacing: { after: 240 },
          }),

          // Property address (second page)
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: `Property Address `,
                bold: true,
              }),
              new TextRun({
                text: `${buildingData.address} ${buildingData.city}, ${buildingData.state} ${buildingData.zip_code}`,
              }),
            ],
            spacing: { after: 60 },
          }),
          // new Paragraph({
          //   alignment: AlignmentType.CENTER,
          //   children: [
          //     new TextRun({
          //       text: `${buildingData.address}`,
          //       bold: true,
          //     }),
          //   ],
          //   spacing: { after: 60 },
          // }),

          // new Paragraph({
          //   alignment: AlignmentType.CENTER,
          //   children: [
          //     new TextRun({
          //       text: `${buildingData.city}, ${buildingData.state} ${buildingData.zip_code}`,
          //     }),
          //   ],
          //   spacing: { after: 120 },
          // }),

          // CompliNY Account info
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: `CompliNY Account: ${clientData.company_name || ""}`,
              }),
            ],
            spacing: { after: 120 },
          }),

          // Agent Owner info (second page)
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: "Agent/Owner :",
                bold: true,
              }),
              new TextRun({
                text: `${clientData.user.name}, ${clientData.address} ${clientData?.city || ""}, ${clientData?.state || ""} ${clientData?.zip_code || ""}`,
              }),
            ],
            spacing: { after: 60 },
          }),

          

          

          // Package printed date
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: `Package Printed on: ${moment().format("MM/DD/YYYY")}`,
              }),
            ],
            spacing: { after: 240 },
          }),

          // Units list table
          createProofOfMailingTable(unitsData, buildingData, clientData),

        new Paragraph({
          alignment: AlignmentType.LEFT,
          children: [
            new TextRun({
              text: `Total notices sent  - ${unitsData.length}`,
              bold: true,
            }),
          ],
          spacing: { after: 240 },
        }),
        ],
      },
    ],
  });

  // Generate document
  const blob = await Packer.toBlob(doc);
  saveAs(blob, `Affidavit_of_Mailing_${buildingData.address.replace(/,|\s/g, '_')}.docx`);
};
